import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAdmin } from '@/context/AdminContext'
import { 
  HomeIcon,
  ShoppingBagIcon,
  ClipboardDocumentListIcon,
  UsersIcon,
  ChartBarIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  BellIcon
} from '@heroicons/react/24/outline'
import Button from '@/components/UI/Button'
import Logo from '@/components/UI/Logo'

/**
 * AdminHeader - Admin-specific header with admin navigation
 * 
 * @component
 */
const AdminHeader = () => {
  const { adminUser, logoutAdmin } = useAdmin()
  const location = useLocation()
  const navigate = useNavigate()

  const handleLogout = () => {
    logoutAdmin()
    navigate('/admin/login')
  }

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const adminNavItems = [
    { name: 'Dashboard', path: '/admin', icon: HomeIcon },
    { name: 'Products', path: '/admin/products', icon: ShoppingBagIcon },
    { name: 'Orders', path: '/admin/orders', icon: ClipboardDocumentListIcon },
    { name: 'Customers', path: '/admin/customers', icon: UsersIcon },
    { name: 'Reports', path: '/admin/reports', icon: ChartBarIcon },
    { name: 'Settings', path: '/admin/settings', icon: CogIcon }
  ]

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <div className="container-custom">
        {/* Top bar */}
        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div className="flex items-center space-x-4">
            <Link to="/admin">
              <Logo 
                size="md" 
                showText={true} 
                textSize="md"
                variant="admin"
                className="hover:scale-105 transition-transform duration-300"
              />
            </Link>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <BellIcon className="w-4 h-4" />
              <span>Welcome, {adminUser?.name || 'Admin'}</span>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="flex items-center space-x-2 text-red-600 border-red-600 hover:bg-red-50"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              <span>Logout</span>
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="py-4">
          <div className="flex items-center space-x-1">
            {adminNavItems.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.name}</span>
                </Link>
              )
            })}
          </div>
        </nav>
      </div>
    </header>
  )
}

export default AdminHeader
